/**
 * API utility to handle function calls from branch deploys
 *
 * Branch deploys don't include serverless functions, so we need to
 * call the main site's functions instead when on a branch deploy.
 */

// Main site URL where functions are deployed
const MAIN_SITE_URL = 'https://trustcenterpage.com'

/**
 * Detect if we're on a branch deploy vs main site
 */
export function isBranchDeploy(): boolean {
  if (typeof window === 'undefined') return false

  const hostname = window.location.hostname

  console.log('[API] isBranchDeploy check:', {
    hostname,
    hasDoubleDash: hostname.includes('--'),
    isPlatformAdmin: hostname.startsWith('platformadmin--'),
    isTenantBranch: /^[a-z0-9]+-(?:live|preview|last)-\d{8}--/.test(hostname)
  })

  // Branch deploy format: {branch}--{site}.trustcenter.page
  // Main site format: {site}.trustcenter.page

  // Exception: platformadmin branch has its own functions
  if (hostname.startsWith('platformadmin--')) {
    console.log('[API] Platform admin branch - has own functions')
    return false
  }

  // Tenant branches (e.g., trustcenter-live-22749352.trustcenterpage.com) are separate deployments
  // They have their own functions and should NOT redirect to main site
  // Only platformadmin branches and double-dash previews are considered "branch deploys" that redirect

  const result = hostname.includes('--')
  console.log('[API] isBranchDeploy result:', result)
  return result
}

/**
 * Install fetch interceptor to:
 * 1. Redirect API calls from branch deploys to main site
 * 2. Handle session timeouts globally (401 responses)
 * 3. Add Authorization headers automatically
 */
export function installFetchInterceptor() {
  if (typeof window === 'undefined') return
  if ((window as any).__fetchInterceptorInstalled) return
  (window as any).__fetchInterceptorInstalled = true

  const originalFetch = window.fetch
  const onBranchDeploy = isBranchDeploy()

  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    let url: string

    if (typeof input === 'string') {
      url = input
    } else if (input instanceof URL) {
      url = input.href
    } else if (input instanceof Request) {
      url = input.url
    } else {
      url = String(input)
    }

    // Check if this is an API call
    const isApiCall = url.startsWith('/api/') || url.startsWith('/.netlify/functions/')

    // If on branch deploy, redirect API calls to main site
    if (onBranchDeploy && isApiCall) {
      url = `${MAIN_SITE_URL}${url}`

      // Ensure credentials are included for cross-origin API calls
      init = {
        ...init,
        credentials: 'include'
      }
    }

    // For API calls, automatically add Authorization header if token exists
    if (isApiCall || (url.includes(MAIN_SITE_URL) && url.includes('/api/'))) {
      const token = localStorage.getItem('auth-token')
      if (token) {
        // Add Authorization header
        const headers = new Headers(init?.headers || {})
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${token}`)
        }

        init = {
          ...init,
          headers,
          credentials: init?.credentials || 'include'
        }
      }
    }

    // Make the request
    const response = await originalFetch.call(this, url, init)

    // Global 401 handler for session timeouts
    if (response.status === 401 && isApiCall) {
      const currentPath = window.location.pathname

      // Check if this is a tenant site (branch deploy)
      // @ts-ignore - __TENANT_CONFIG__ is injected at build time for branch deploys
      const isTenantSite = typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__?.tenantId
      // @ts-ignore
      const isLiveSite = typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__.version === 'live'

      // Don't redirect if already on login/auth pages OR if this is a public live site
      if (!currentPath.includes('/login') && !currentPath.includes('/auth/') && !isLiveSite) {
        // On platform sites, SessionManager handles session expiration gracefully
        // On tenant sites (preview/last), we handle it here since SessionManager is disabled
        const sessionManagerEnabled = !isTenantSite

        if (sessionManagerEnabled) {
          // Platform site: Let SessionManager handle cleanup and show graceful modal
          console.log('🔒 Session expired - SessionManager will handle cleanup')
          sessionStorage.setItem('sessionTimedOut', 'true')
        } else {
          // Tenant site: Handle cleanup and redirect immediately
          console.log('🔒 Session expired - cleaning up and redirecting')
          sessionStorage.setItem('sessionTimedOut', 'true')
          localStorage.removeItem('auth-token')
          sessionStorage.removeItem('user-session')
          document.cookie = 'auth-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
          window.location.href = '/login'
        }
      } else if (isLiveSite) {
        console.log('📄 Live site - 401 response will not trigger redirect')
      }
    }

    return response
  }

  if (onBranchDeploy) {
    console.log('🔧 Branch deploy detected - API calls will be redirected to main site')
  }
  console.log('🔐 Global session timeout handler installed')
}

/**
 * Shared fetch utility that automatically includes Authorization headers
 *
 * This ensures all API calls include the JWT token from localStorage,
 * preventing 401 authentication errors after the GIS migration.
 *
 * @param url - API endpoint URL
 * @param options - Fetch options (method, body, etc.)
 * @param tenantOverride - Optional tenant ID override for platform admin
 * @returns Promise<Response>
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
  tenantOverride?: string
): Promise<Response> {
  // Build headers with auth token
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {})
  }

  // Only set Content-Type if not already set and body is not FormData
  // (FormData automatically sets the correct Content-Type with boundary)
  if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  // Add Authorization header if token exists in localStorage
  const token = localStorage.getItem('auth-token')
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // Add tenant override header when managing a specific tenant from platform admin
  if (tenantOverride) {
    headers['X-Tenant-Override'] = tenantOverride
  }

  // Merge options with our headers
  const fetchOptions: RequestInit = {
    ...options,
    credentials: 'include',
    headers
  }

  try {
    const response = await fetch(url, fetchOptions)

    // Handle session timeout (401 Unauthorized)
    if (response.status === 401 && typeof window !== 'undefined') {
      const currentPath = window.location.pathname

      // Check if this is a tenant site (branch deploy)
      // @ts-ignore - __TENANT_CONFIG__ is injected at build time for branch deploys
      const isTenantSite = typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__?.tenantId
      // @ts-ignore
      const isLiveSite = typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__.version === 'live'

      // Don't redirect if already on login/auth pages OR if this is a public live site
      if (!currentPath.includes('/login') && !currentPath.includes('/auth/') && !isLiveSite) {
        // On platform sites, SessionManager handles session expiration gracefully
        // On tenant sites (preview/last), we handle it here since SessionManager is disabled
        const sessionManagerEnabled = !isTenantSite

        if (sessionManagerEnabled) {
          // Platform site: Let SessionManager handle cleanup and show graceful modal
          console.log('🔒 Session expired (fetchWithAuth) - SessionManager will handle cleanup')
          sessionStorage.setItem('sessionTimedOut', 'true')
        } else {
          // Tenant site: Handle cleanup and redirect immediately
          console.log('🔒 Session expired (fetchWithAuth) - cleaning up and redirecting')
          sessionStorage.setItem('sessionTimedOut', 'true')
          localStorage.removeItem('auth-token')
          sessionStorage.removeItem('user-session')
          document.cookie = 'auth-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
          window.location.href = '/login'
        }
      } else if (isLiveSite) {
        console.log('📄 Live site (fetchWithAuth) - 401 response will not trigger redirect')
      }
    }

    return response
  } catch (error) {
    // Ensure all errors (network, parsing, etc.) are propagated to the caller
    // Don't swallow errors - let the calling code handle them appropriately
    throw error
  }
}
